import React from "react";
import Marquee from "../components/Marquee";
import { ABOUT_MORPH_IMAGES, ARTIST_NAMES } from "../data/products";

const KARTIKEY_PHOTO =
  "https://customer-assets.emergentagent.com/job_raw-culture-1/artifacts/m022nitx_Screenshot%202026-05-19%20at%209.54.21%E2%80%AFPM.png";
const KARAN_KANCHAN_PHOTO =
  "https://customer-assets.emergentagent.com/job_raw-culture-1/artifacts/vzjehykz_Screenshot%202026-05-19%20at%209.53.55%E2%80%AFPM.png";

const AboutPage = () => {
  return (
    <div data-testid="page-about" className="bg-charcoal text-cream">
      {/* Hero block */}
      <section className="pt-[120px] px-4 md:px-12 max-w-[1200px] mx-auto">
        <div className="flex items-center gap-4 mb-10">
          <span className="flex-1 h-px bg-cream/30" />
          <h1 className="font-display uppercase text-5xl sm:text-7xl md:text-8xl">About Me</h1>
          <span className="flex-1 h-px bg-cream/30" />
        </div>
        <div className="w-full border border-cream/20 bg-charcoal" style={{ aspectRatio: "16 / 9" }}>
          <img
            src={KARAN_KANCHAN_PHOTO}
            alt="Kartikey with Karan Kanchan"
            loading="eager"
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="mt-10 max-w-3xl mx-auto space-y-6 font-body text-xl md:text-2xl leading-relaxed text-cream/90">
          <p>
            "Damage culture" can be understood as a shift in attitude where breaking rules, aesthetics, or expectations is seen as a form of expression. It shows up in how people dress, create, consume, or communicate. Embracing imperfection is a style. Rather than being about harm, it reflects a reaction to polished, overly controlled environments, making space for rawness and individuality. In this sense, "Damage culture" isn't just about destruction. It's about reinterpreting what's considered normal or valuable.
          </p>
          <p>
            <span className="text-dc-red">Damage culture</span> is also a double entendre, where "damage culture" can be seen as <em>damaging the culture</em>. In this sense, culture is the norm, the current state of things as it is.
          </p>
        </div>
      </section>

      {/* Meet the founder */}
      <section className="px-4 md:px-12 py-20 md:py-28 max-w-[1200px] mx-auto">
        <h2 className="font-display uppercase text-4xl sm:text-6xl md:text-7xl mb-12">
          Meet the founder
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-10 md:gap-16 items-start">
          <div className="font-body text-lg md:text-xl leading-relaxed text-cream/85 space-y-5">
            <p>
              Damage Culture started in 2025 in a small workshop in India. It began as a refusal. A refusal of factory-perfect seams, mass-printed graphics, and clothes that look like every other clothes.
            </p>
            <p>
              We cut, dye, distress and finish by hand. Some pieces take a day. Some take two weeks. The exclusives sometimes take longer than we'd like to admit.
            </p>
            <p>
              We're not interested in being clean. We're interested in being honest.
            </p>
          </div>
          <div className="card-surface bg-cream-soft border border-cream/30 p-5 md:p-6 w-full md:w-[300px]">
            <div
              className="w-full border border-cream/30 overflow-hidden"
              style={{ aspectRatio: "3 / 4" }}
            >
              <img
                src={KARTIKEY_PHOTO}
                alt="Kartikey, founder of Damage Culture"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-ink mt-4 space-y-2">
              <p>KARTIKEY / FOUNDER</p>
              <p className="opacity-70">CUTS, DYES, DISTRESSES. BUILDS THE CULTURE BY DAMAGING IT.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Artists we've worked with */}
      <section className="py-16 md:py-20">
        <div className="px-4 md:px-12 max-w-[1200px] mx-auto mb-8">
          <h2 className="font-display uppercase text-4xl sm:text-6xl md:text-7xl">
            Artists we've worked with
          </h2>
        </div>
        <Marquee items={ARTIST_NAMES} variant="transparent" speed="slow" />

        {/* Featured collab: Karan Kanchan */}
        <div className="px-4 md:px-12 max-w-[1200px] mx-auto mt-14 md:mt-20">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-8 md:gap-12 items-center">
            <div className="border border-cream/20 overflow-hidden">
              <img
                src={KARAN_KANCHAN_PHOTO}
                alt="Kartikey with Karan Kanchan"
                className="w-full h-full object-cover"
                style={{ aspectRatio: "4 / 3" }}
              />
            </div>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-widest text-dc-red mb-3">
                FEATURED COLLAB / 01
              </p>
              <h3 className="font-display uppercase text-4xl sm:text-5xl md:text-6xl leading-none">
                Karan Kanchan
              </h3>
              <p className="font-body text-lg md:text-xl mt-5 text-cream/85 leading-relaxed">
                Producer. Outlier. The sound behind some of the loudest moments in Indian hip-hop. Dressed in custom Damage Culture pink-camo. Photographed alongside our founder.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Design philosophy */}
      <section className="px-4 md:px-12 py-20 md:py-28 max-w-[1200px] mx-auto">
        <h2 className="font-display uppercase text-4xl sm:text-6xl md:text-7xl mb-10">
          Design philosophy
        </h2>
        <div className="font-body text-xl md:text-2xl leading-relaxed text-cream/90 space-y-6 max-w-3xl">
          <p>
            Wear is not damage. Wear is honesty. A piece that's been lived in carries a story you can't fake on a factory floor.
          </p>
          <p>
            We treat decay as material. Distress as composition. Imperfection as the only honest finish. Each piece is built to outlive the trend it ignores.
          </p>
          <p className="text-dc-red italic">
            If it looks new in a year, we did it wrong.
          </p>
        </div>
      </section>

      {/* Red credit block (closing) */}
      <section className="bg-dc-red text-cream px-4 md:px-12 py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto">
          <p className="font-mono text-xs uppercase tracking-widest opacity-80 mb-4">
            &copy;DC 2025 / DAMAGING THE CULTURE
          </p>
          <h3 className="font-display uppercase text-4xl sm:text-6xl md:text-8xl leading-[0.95]">
            WE DESIGN FOR THE OUTCASTS,<br />
            THE RIOTS, THE QUIET ONES.
          </h3>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
